// Liten fetch-wrapper för Loupe-API:et.
//
// Designmål:
// - Same-origin via Vite-proxy → cookies skickas automatiskt med
//   `credentials: 'include'` (extra säkerhet).
// - JSON in/ut som default. Multipart/binär kan skickas via `raw: true`.
// - Kasta `ApiError` med statuskod + ev. body-felkod så att React Query kan
//   skilja på 401 (unauth) och 5xx (server) och rendera rätt fallback.

const API_BASE = '/api';

export class ApiError extends Error {
  public status: number;
  public code?: string;
  public body?: unknown;

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  // Skicka rå body utan JSON.stringify (ex. FormData)
  raw?: boolean;
  // Sökparametrar — undefined-värden ignoreras.
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(API_BASE + path, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  // Ta endast pathname + search (vi pratar same-origin via Vite-proxy)
  return url.pathname + url.search;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, raw, query, signal } = opts;

  const headers: Record<string, string> = {};
  let payload: BodyInit | undefined;

  if (body !== undefined) {
    if (raw) {
      payload = body as BodyInit;
    } else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: payload,
      credentials: 'include',
      signal,
    });
  } catch (err) {
    // Nätverksfel — backend nere etc.
    throw new ApiError(err instanceof Error ? err.message : 'Network error', 0);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const data: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    let code: string | undefined;
    let message = `HTTP ${response.status}`;
    if (isJson && data && typeof data === 'object' && 'error' in data) {
      const errObj = (data as { error?: { code?: string; message?: string } }).error;
      code = errObj?.code;
      if (errObj?.message) message = errObj.message;
    }
    throw new ApiError(message, response.status, code, data);
  }

  return data as T;
}

// Convenience wrappers
export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};

// Hjälp att bygga bild-URL:er som kan stoppas in i <img src=...>.
// Backend levererar bilder via cookie-auth — same-origin gör det transparent.
export function imageUrl(
  imageId: string,
  size: 'thumb' | 'preview' | 'full' = 'preview',
): string {
  // Vi har två kandidater enligt task-instruktionen och API_DESIGN.md:
  //   /api/images/:id/file?size=X   (task-instruktion)
  //   /img/:id?size=X               (API_DESIGN.md)
  // Vi använder task-instruktionens variant per default — Vite proxy:ar båda.
  return `/api/images/${imageId}/file?size=${size}`;
}
